/**
 * 多模型管理器
 * 支持同時加載和管理多個 Fragments 模型
 */

import * as THREE from "three";
import * as FRAGS from "@thatopen/fragments";
import mapboxgl from "mapbox-gl";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// ========================================
// 模型配置接口
// ========================================

export interface ModelConfig {
  id: string;                    // 模型唯一ID
  name: string;                  // 模型顯示名稱
  url: string;                   // 模型文件URL（或 API endpoint）
  fileId?: string;               // R2 文件 ID (用於獲取簽名 URL)
  folder?: string;               // 所屬資料夾（用於分類管理）
  folderName?: string;           // 資料夾名稱（用於顯示）
  position: {
    lng: number;                 // 經度
    lat: number;                 // 緯度
    altitude?: number;           // 海拔高度（公尺）
  };
  rotation?: {
    x?: number;                  // X軸旋轉（弧度）
    y?: number;                  // Y軸旋轉（弧度）
    z?: number;                  // Z軸旋轉（弧度）
  };
  scale?: number;                // 縮放比例
  offset?: {
    x?: number;                  // X軸偏移
    y?: number;                  // Y軸偏移
    z?: number;                  // Z軸偏移
  };
  visible?: boolean;             // 初始是否可見
  color?: string;                // 模型標記顏色（用於地圖標記）
  modelType?: 'frag' | 'gltf';   // 模型類型
  metadata?: {                   // 額外元數據
    description?: string;
    tags?: string[];
    createdAt?: string;
    updatedAt?: string;
    [key: string]: any;
  };
}

// ========================================
// 加載的模型信息
// ========================================

interface LoadedModelInfo {
  config: ModelConfig;
  model: FRAGS.FragmentsModel | THREE.Group;
  modelType: 'frag' | 'gltf';
  transform: {
    translateX: number;
    translateY: number;
    translateZ: number;
    rotateX: number;
    rotateY: number;
    rotateZ: number;
    scale: number;
  };
  visible: boolean;
  // Marker 現在存在 folderMarkers 中，每個 Folder 一個
}

// ========================================
// 頂點顏色管理（內嵌實現）
// ========================================

export interface MeshColorData {
  mesh: THREE.Mesh;
  colorAttribute: THREE.BufferAttribute;
  localIdToVertices: Map<number, number[]>;
}

interface VertexColorOptions {
  color: THREE.Color | string;
  opacity?: number;
}

// ========================================
// 多模型管理器類別
// ========================================

export class MultiModelManager {
  private fragments: FRAGS.FragmentsModels;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private map: mapboxgl.Map;
  private models: Map<string, LoadedModelInfo>;
  private workerUrl: string;
  
  // Folder Marker 存儲
  private folderMarkers: Map<string, {marker: mapboxgl.Marker, point: mapboxgl.Marker, models: ModelConfig[]}> = new Map();
  
  // 頂點顏色管理數據（內嵌實現）
  private vertexColorData: Map<string, MeshColorData[]> = new Map();
  private coloredElements: Map<string, Map<number, VertexColorOptions>> = new Map();
  
  // 預先下載的 frag buffer（並行下載 + 序列 fragments.load() 用）
  // 並行下載完成後，loadFragModel 會直接從這裡拿 buffer 而不重新 fetch
  private prefetchedBuffers: Map<string, ArrayBuffer> = new Map();
  
  constructor(
    workerUrl: string,
    scene: THREE.Scene,
    camera: THREE.Camera,
    map: mapboxgl.Map
  ) {
    this.workerUrl = workerUrl;
    this.scene = scene;
    this.camera = camera;
    this.map = map;
    this.models = new Map();
    
    // 初始化 Fragments
    this.fragments = new FRAGS.FragmentsModels(workerUrl);
    
    // 防止 tree-shaking 移除方法
    // 確保這些方法在構建後仍然存在
    if (false) {
      this.setVertexColor("", [], { color: "#000000" });
      this.resetVertexColor("");
      this.setAllModelsTransparency(0.2);
    }
    
  }
  
  // ========================================
  // 立即捕獲模型數據（在 load 後）
  // ========================================
  
  private captureModelIdData(modelId: string, model: FRAGS.FragmentsModel): void {
    const meshDataList: MeshColorData[] = [];
    let totalMeshes = 0;
    let capturedMeshes = 0;
    
    model.object.traverse((child) => {
      if (!(child as THREE.Mesh).isMesh) return;
      
      totalMeshes++;
      const mesh = child as THREE.Mesh;
      const geometry = mesh.geometry;
      
      const idAttr = geometry.attributes.id as THREE.BufferAttribute;
      if (!idAttr) {
        console.warn(`  ⚠️ Mesh ${totalMeshes} 沒有 id attribute`);
        return;
      }
      
      // 檢查 array 是否存在
      if (!idAttr.array) {
        console.error(`  ❌ Mesh ${totalMeshes} 的 id.array 已被刪除！`);
        return;
      }
      
      // 立即複製 id 數據到本地
      const localIdToVertices = new Map<number, number[]>();
      
      for (let i = 0; i < idAttr.count; i++) {
        const localId = idAttr.array[i];
        
        if (!localIdToVertices.has(localId)) {
          localIdToVertices.set(localId, []);
        }
        localIdToVertices.get(localId)!.push(i);
      }
      
      // 確保有 color attribute
      let colorAttr = geometry.attributes.color as THREE.BufferAttribute;
      
      if (!colorAttr) {
        const posCount = geometry.attributes.position.count;
        const colorArray = new Float32Array(posCount * 3);
        colorArray.fill(1); // 默認白色
        
        colorAttr = new THREE.BufferAttribute(colorArray, 3);
        geometry.setAttribute('color', colorAttr);
        
        // 啟用頂點顏色
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials.forEach((mat) => {
          if (mat) {
            (mat as any).vertexColors = true;
            mat.needsUpdate = true;
          }
        });
      }
      
      // 保存數據
      meshDataList.push({
        mesh,
        colorAttribute: colorAttr,
        localIdToVertices
      });
      
      capturedMeshes++;
    });
    
    this.vertexColorData.set(modelId, meshDataList);
    this.coloredElements.set(modelId, new Map());
  }
  
  // 暴露給 colorController 使用
  public getVertexColorData(modelId: string): MeshColorData[] | undefined {
    return this.vertexColorData.get(modelId);
  }
  
  // ========================================
  // 加載單個模型
  // ========================================
  
  async loadModel(config: ModelConfig): Promise<FRAGS.FragmentsModel | THREE.Group> {
    // 判斷模型類型
    let modelType = config.modelType;
    if (!modelType) {
      // 優先從 fileId 的原始檔名判斷
      if ((config as any).fileId && (config as any).originalFileName) {
        const ext = (config as any).originalFileName.split('.').pop()?.toLowerCase();
        modelType = (ext === 'gltf' || ext === 'glb') ? 'gltf' : 'frag';
      } else {
        // 從 URL 判斷
        const ext = config.url.split('.').pop()?.toLowerCase();
        modelType = (ext === 'gltf' || ext === 'glb') ? 'gltf' : 'frag';
      }
      
      // 寫回 config,確保下次載入時不會丟失
      config.modelType = modelType;
    }
    
    if (modelType === 'gltf') {
      return this.loadGLTFModel(config);
    } else {
      return this.loadFragModel(config);
    }
  }
  
  private async loadFragModel(config: ModelConfig): Promise<FRAGS.FragmentsModel> {
    try {
      // 檢查模型是否已存在
      if (this.models.has(config.id)) {
        console.warn(`⚠️ Model ${config.id} already exists`);
        return this.models.get(config.id)!.model as FRAGS.FragmentsModel;
      }
      
      // 優先使用 prefetched buffer（從 loadModels Phase 1 並行下載得到的）
      let buffer = this.prefetchedBuffers.get(config.id);
      
      if (!buffer) {
        // Fallback：沒有預先下載（單獨呼叫 loadModel 時走這條），自己下載
        let modelUrl = config.url;
        
        if ((config as any).fileId) {
          try {
            const { getFileSignedUrl } = await import('./r2UploadClient');
            modelUrl = await getFileSignedUrl((config as any).fileId, 3600);
          } catch (error) {
            console.error(`❌ Failed to get signed URL:`, error);
            throw new Error(`無法獲取文件訪問權限: ${error}`);
          }
        }
        
        const file = await fetch(modelUrl);
        if (!file.ok) {
          if (file.status === 404) {
            const fileName = config.url.split('/').pop() || 'unknown';
            throw new Error(`File not found: ${modelUrl}\n\nPlease ensure the file "${fileName}" exists.`);
          }
          if (file.status === 403) {
            throw new Error(`Access denied: Access permission expired or invalid, please reload the page`);
          }
          throw new Error(`HTTP error! status: ${file.status}`);
        }
        
        buffer = await file.arrayBuffer();
      } else {
        // 用完即釋放，避免同個 buffer 滯留兩處
        this.prefetchedBuffers.delete(config.id);
      }
      
      // 檢查文件大小（空文件或錯誤響應）
      if (buffer.byteLength === 0) {
        throw new Error(`File is empty: ${config.url}. Please check if the file exists and is not corrupted.`);
      }
      
      // 檢查文件頭：如果返回的是 HTML（通常是 404 頁面），提供更清晰的錯誤信息
      if (buffer.byteLength < 1000) {
        const decoder = new TextDecoder();
        const preview = decoder.decode(buffer.slice(0, Math.min(500, buffer.byteLength)));
        if (preview.includes('<!DOCTYPE') || preview.includes('<html') || preview.includes('404') || preview.includes('Not Found')) {
          const fileName = config.url.split('/').pop() || 'unknown';
          throw new Error(`File not found: ${config.url}\n\nThe server returned an HTML error page instead of the model file.\n\nPlease ensure the file "${fileName}" is copied to the public/model/ directory.`);
        }
      }
      
      // 加載模型
      const model = await this.fragments.load(buffer, { modelId: config.id });
      
      // ⚠️ 關鍵：load() 返回後，立即捕獲 id 數據（在被刪除之前）
      this.captureModelIdData(config.id, model);
      
      // 計算變換
      const altitude = config.position.altitude || 0;
      const rotation = config.rotation || {};
      const scale = config.scale || 1;
      const offset = config.offset || { x: 0, y: 0, z: 0 };
      
      // 移除詳細的配置日誌以提升性能（僅在開發時需要）
      // console.log(`📐 Loading model ${config.id} with config:`, {
      //   position: config.position,
      //   offset: offset,
      //   rotation: rotation,
      //   scale: scale
      // });
      
      const modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(
        [config.position.lng, config.position.lat],
        altitude
      );
      
      const transform = {
        translateX: modelAsMercatorCoordinate.x,
        translateY: modelAsMercatorCoordinate.y,
        translateZ: modelAsMercatorCoordinate.z || 0,
        rotateX: rotation.x !== undefined ? rotation.x : Math.PI / 2,
        rotateY: rotation.y || 0,
        rotateZ: rotation.z || 0,
        scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits() * scale
      };
      
      // 禁用視錐剔除（確保模型完整顯示）
      // Fake Camera 會在 main_multi_model.ts 中統一設置
      this.disableCulling(model);
      
      // 直接將模型添加到場景（不使用 Group）
      this.scene.add(model.object);
      
      // 應用位置偏移（在 Three.js 本地坐標系中）
      // offset 現在同時影響模型位置和視錐體相機位置
      // 注意：需要檢查 undefined，因為 0 也是有效值
      const initialPosition = {
        x: model.object.position.x,
        y: model.object.position.y,
        z: model.object.position.z
      };
      
      if (offset.x !== undefined && offset.x !== null) {
        model.object.position.x += offset.x;
      }
      if (offset.y !== undefined && offset.y !== null) {
        model.object.position.y += offset.y;
      }
      if (offset.z !== undefined && offset.z !== null) {
        model.object.position.z += offset.z;
      }
      
      // 純診斷 log（零邏輯改動）— 攤平成 string 避免 Console 摺疊 Object
      const fmt = (n: number) => (n === undefined || n === null) ? 'NA' : Number(n).toExponential(6);
      console.log(
        `[FRAG-LOAD] ${config.id} | ` +
        `name=${config.name || 'noname'} | ` +
        `lng=${config.position.lng} lat=${config.position.lat} alt=${altitude} | ` +
        `fragInit=(${fmt(initialPosition.x)},${fmt(initialPosition.y)},${fmt(initialPosition.z)}) | ` +
        `cfgOff=(${offset.x || 0},${offset.y || 0},${offset.z || 0}) | ` +
        `final=(${fmt(model.object.position.x)},${fmt(model.object.position.y)},${fmt(model.object.position.z)}) | ` +
        `mercator=(${fmt(transform.translateX)},${fmt(transform.translateY)},${fmt(transform.translateZ)}) | ` +
        `scale=${fmt(transform.scale)} | ` +
        `mapSize=${this.models.size} sceneKids=${this.scene.children.length}`
      );
      
      // 強制更新矩陣
      model.object.updateMatrix();
      model.object.updateMatrixWorld(true);
      
      // 設置可見性
      const visible = config.visible !== false;
      model.object.visible = visible;
      
      // 不在這裡創建 Marker，會在 loadModels 完成後統一為每個 Folder 創建
      
      // 保存模型信息
      const modelInfo: LoadedModelInfo = {
        config,
        model,
        modelType: 'frag',
        transform,
        visible
      };
      
      this.models.set(config.id, modelInfo);
      
      // ⚠️ 關鍵：在 update() 之前禁用所有 BufferAttribute 的 onUploadCallback
      // 防止 GPU 上傳後刪除 array
      model.object.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh) return;
        const mesh = child as THREE.Mesh;
        const geometry = mesh.geometry;
        
        // 禁用所有 attribute 的 onUploadCallback
        Object.keys(geometry.attributes).forEach(key => {
          const attr = geometry.attributes[key];
          if (attr && (attr as THREE.BufferAttribute).onUploadCallback) {
            Object.defineProperty(attr, 'onUploadCallback', {
              value: function() { /* 不刪除 array */ },
              writable: false,
              configurable: false
            });
          }
        });
      });
      
      // 更新 Fragments（重新啟用）
      this.fragments.update(true);
      
      // 載入後立即強制禁用所有 culling 和 LOD
      this.disableCulling(model);
      
      this.map.triggerRepaint();
      
      return model;
      
    } catch (error) {
      console.error(`❌ Failed to load Frag model: ${config.name}`, error);
      throw error;
    }
  }
  
  private async loadGLTFModel(config: ModelConfig): Promise<THREE.Group> {
    try {
      if (this.models.has(config.id)) {
        console.warn(`⚠️ Model ${config.id} already exists`);
        return this.models.get(config.id)!.model as THREE.Group;
      }
      
      let modelUrl = config.url;
      if ((config as any).fileId) {
        const { getFileSignedUrl } = await import('./r2UploadClient');
        modelUrl = await getFileSignedUrl((config as any).fileId, 3600);
      }
      
      const loader = new GLTFLoader();
      
      const gltf = await new Promise<any>((resolve, reject) => {
        loader.load(modelUrl, resolve, undefined, reject);
      });
      
      const model = gltf.scene;
      
      model.name = `gltf-${config.id}`;
      model.userData.modelId = config.id;
      model.userData.modelName = config.name;
      model.userData.fileId = (config as any).fileId;
      
      const altitude = config.position.altitude || 0;
      const scale = config.scale || 100;
      const rotation = config.rotation || {};
      
      const modelOrigin: [number, number] = [config.position.lng, config.position.lat];
      const modelRotate = [
        rotation.x !== undefined ? rotation.x : Math.PI / 2,
        rotation.y !== undefined ? rotation.y : 0,
        rotation.z !== undefined ? rotation.z : 0
      ];
      
      const modelAsMercatorCoordinate = mapboxgl.MercatorCoordinate.fromLngLat(modelOrigin, altitude);
      
      const modelTransform = {
        translateX: modelAsMercatorCoordinate.x,
        translateY: modelAsMercatorCoordinate.y,
        translateZ: modelAsMercatorCoordinate.z,
        rotateX: modelRotate[0],
        rotateY: modelRotate[1],
        rotateZ: modelRotate[2],
        scale: modelAsMercatorCoordinate.meterInMercatorCoordinateUnits() * scale
      };
      
      model.visible = config.visible !== false;
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          (child as THREE.Mesh).frustumCulled = false;
        }
      });
      
      const modelInfo: LoadedModelInfo = {
        config,
        model,
        modelType: 'gltf',
        transform: modelTransform,
        visible: model.visible
      };
      
      this.models.set(config.id, modelInfo);
      return model;
    } catch (error) {
      console.error(`❌ Failed to load GLTF model: ${config.name}`, error);
      throw error;
    }
  }
  
  // ========================================
  // 批量加載多個模型
  // ========================================
  
  async loadModels(
    configs: ModelConfig[],
    onProgress?: (info: {
      phase: 'prefetch' | 'parse';
      done: number;
      total: number;
      percent: number; // 0-100 across both phases combined
      currentName?: string;
      cached?: boolean; // only meaningful for phase='prefetch'
    }) => void
  ): Promise<void> {
    // 修復「每次 page load frag model 偏移程度不同」bug：
    // @thatopen/fragments 的 FragmentsModels 在多模型載入時會把第一個 load 完成的 model
    // 當作世界原點。並行 load → 網路 race 決定誰先完成 → F5 每次第一個不同 → 偏移隨機。
    //
    // 修法（最新版）：
    //   Phase 1: 並行下載所有 frag buffer（網路 I/O，瓶頸）
    //   Phase 2: 序列 load 第一個（鎖住世界原點），其餘並行 + concurrency cap（CPU 解析）
    // 第一個永遠是同一個 → fragInit 永遠固定 → 偏移可預期。
    // 其餘 49 個並行解析 → 速度接近 N 倍。
    const sortedConfigs = [...configs].sort((a, b) => a.id.localeCompare(b.id));

    console.log(`[LOAD-ORDER] sorted ids:`, sortedConfigs.map((c, i) => `${i}:${c.id.slice(-6)}(${c.name || '?'})`).join(' | '));

    const fragConfigs = sortedConfigs.filter(c => {
      let mt = c.modelType;
      if (!mt) {
        const ext = ((c as any).originalFileName || c.url || '').split('.').pop()?.toLowerCase();
        mt = (ext === 'gltf' || ext === 'glb') ? 'gltf' : 'frag';
      }
      return mt === 'frag';
    });

    // Each file contributes 2 progress events (prefetch + parse). Total
    // events = 2 × fragConfigs.length + 1 × gltfConfigs.length. We map
    // them all to 0-100 % so the bar moves smoothly across both phases.
    const totalEvents = fragConfigs.length * 2 + (sortedConfigs.length - fragConfigs.length);
    let doneEvents = 0;
    const fire = (phase: 'prefetch' | 'parse', name?: string, cached?: boolean) => {
      if (!onProgress) return;
      doneEvents++;
      onProgress({
        phase,
        done: doneEvents,
        total: totalEvents,
        percent: Math.min(100, Math.round((doneEvents / totalEvents) * 100)),
        currentName: name,
        cached,
      });
    };

    // Phase 1 — parallel download (cache-aware: hits skip network)
    let cacheHits = 0;
    if (fragConfigs.length > 0) {
      console.log(`⏬ Prefetching ${fragConfigs.length} frag buffers in parallel...`);
      const t0 = performance.now();
      await Promise.allSettled(
        fragConfigs.map(async (c) => {
          let cached = false;
          try {
            cached = await this.prefetchFragBuffer(c);
            if (cached) cacheHits++;
          } finally {
            fire('prefetch', c.name, cached);
          }
        })
      );
      console.log(`⏬ Prefetch done in ${(performance.now() - t0).toFixed(0)}ms (${this.prefetchedBuffers.size} buffers ready, ${cacheHits} from cache)`);
    }

    // Phase 2 — load. First model serially (locks the world origin),
    // remainder in parallel with a concurrency cap so we don't melt
    // the main thread parsing 50 frag files at once.
    const errors: { config: ModelConfig; reason: any }[] = [];
    const t1 = performance.now();
    if (sortedConfigs.length > 0) {
      const first = sortedConfigs[0];
      try {
        await this.loadModel(first);
      } catch (e) {
        errors.push({ config: first, reason: e });
      }
      fire('parse', first.name);

      const rest = sortedConfigs.slice(1);
      if (rest.length > 0) {
        const PARSE_CONCURRENCY = Math.max(
          2,
          Math.min(6, ((navigator as any)?.hardwareConcurrency || 4) - 1)
        );
        console.log(`🏗️  Parsing ${rest.length} remaining models in parallel (concurrency=${PARSE_CONCURRENCY})...`);
        let cursor = 0;
        await Promise.all(
          Array.from({ length: Math.min(PARSE_CONCURRENCY, rest.length) }, async () => {
            while (cursor < rest.length) {
              const idx = cursor++;
              const cfg = rest[idx];
              try {
                await this.loadModel(cfg);
              } catch (e) {
                errors.push({ config: cfg, reason: e });
              }
              fire('parse', cfg.name);
            }
          })
        );
      }
    }
    console.log(`🏗️  Parse done in ${(performance.now() - t1).toFixed(0)}ms`);

    errors.forEach(({ config, reason }) => {
      console.error(`❌ Model load failed: ${config.name}`, reason);
    });

    this.prefetchedBuffers.clear();
    this.createFolderMarkers(configs);
  }
  
  /**
   * 並行下載 frag buffer 用，結果暫存在 prefetchedBuffers
   * loadFragModel 會優先從 prefetchedBuffers 拿 buffer
   */
  /**
   * Resolve a buffer for a frag config.
   *
   * Order of preference, fastest → slowest:
   *   1. IndexedDB cache (shared with the viewer page via
   *      src/modelBufferCache.ts — DB "echov-viewer-cache")
   *   2. Network fetch of config.url (caller batch-signs URLs upfront)
   *   3. Last-resort per-file re-sign + fetch (only if config.url
   *      was never populated)
   *
   * On a successful network fetch we write the buffer back to the
   * cache so the NEXT app reload (or viewer open) is instant.
   *
   * Returns true if the buffer came from cache (so the caller can
   * label progress as "Cached" instead of "Downloading").
   */
  private async prefetchFragBuffer(config: ModelConfig): Promise<boolean> {
    const cacheKey = (config as any).fileId || config.id;
    const cacheUserId = this.getCacheUserId();

    // 1) Cache lookup first — instant if present.
    try {
      const { viewerCacheGet } = await import('./modelBufferCache');
      const hit = await viewerCacheGet(cacheUserId, cacheKey);
      if (hit && hit.buffer && hit.buffer.byteLength > 0) {
        this.prefetchedBuffers.set(config.id, hit.buffer);
        return true;
      }
    } catch (err) {
      console.warn('[prefetchFragBuffer] cache lookup failed (continuing to network):', err);
    }

    // 2) Network fetch.
    try {
      let modelUrl = config.url;
      if (!modelUrl && (config as any).fileId) {
        console.warn(`[prefetchFragBuffer] no url on ${config.name}; falling back to per-file sign`);
        const { getFileSignedUrl } = await import('./r2UploadClient');
        modelUrl = await getFileSignedUrl((config as any).fileId, 3600);
        config.url = modelUrl;
      }
      if (!modelUrl) {
        throw new Error(`No url available for ${config.name || config.id}`);
      }
      const res = await fetch(modelUrl);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status} fetching ${modelUrl}`);
      }
      const buffer = await res.arrayBuffer();
      if (buffer.byteLength === 0) {
        throw new Error(`Empty buffer for ${config.name || config.id}`);
      }
      this.prefetchedBuffers.set(config.id, buffer);

      // 3) Best-effort cache write so the next page open is a hit.
      // Fire-and-forget — never block the load on a cache write.
      try {
        const { viewerCacheSet } = await import('./modelBufferCache');
        void viewerCacheSet(cacheUserId, cacheKey, buffer, {
          modelType: 'frag',
          name: config.name,
          etag: res.headers.get('etag') || undefined,
        });
      } catch { /* noop */ }

      return false;
    } catch (err) {
      console.warn(`⚠️ Prefetch failed for ${config.name || config.id}, will fall back to direct fetch:`, err);
      return false;
    }
  }

  /**
   * User-id namespace for the cache key. Mirrors the viewer's logic so
   * the same byte buffer is found whether the user opens app.html or
   * viewer.html first. Falls back to 'anon' for unauthenticated.
   */
  private getCacheUserId(): string {
    try {
      const raw = sessionStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        return String(u?.id || u?._id || u?.email || 'anon');
      }
    } catch { /* noop */ }
    return 'anon';
  }
  
  /**
   * 為每個 Folder 創建一個 Marker（而不是為每個 model 創建）
   */
  private createFolderMarkers(configs: ModelConfig[]): void {
    // 清除早期創建的 model markers（在 modelManager 初始化之前創建的）
    // 這些是在 loadAndDisplayModelMarkers() 中創建的臨時 markers
    if ((window as any).earlyModelMarkers) {
      (window as any).earlyModelMarkers.forEach((marker: mapboxgl.Marker) => marker.remove());
      (window as any).earlyModelMarkers = [];
    }
    
    // 按 Folder 分組
    const folderGroups = new Map<string, ModelConfig[]>();
    
    configs.forEach(config => {
      const folderName = (config as any).folderName || config.folder || config.name;
      if (!folderGroups.has(folderName)) {
        folderGroups.set(folderName, []);
      }
      folderGroups.get(folderName)!.push(config);
    });
    
    // 為每個 Folder 創建一個 Marker
    folderGroups.forEach((models, folderName) => {
      // 使用第一個模型的位置和顏色作為 Folder Marker 的位置
      const firstModel = models[0];
      if (firstModel.color && firstModel.position) {
        const point = this.createPoint(firstModel);
        const marker = this.createMarker(firstModel);
        
        this.folderMarkers.set(folderName, { marker, point, models });
      }
    });
  }
  
  // ========================================
  // 模型可見性控制
  // ========================================
  
  showModel(modelId: string): void {
    const modelInfo = this.models.get(modelId);
    if (!modelInfo) {
      console.warn(`⚠️ Model not found: ${modelId}`);
      return;
    }
    
    if (modelInfo.modelType === 'gltf') {
      (modelInfo.model as THREE.Group).visible = true;
    } else {
      (modelInfo.model as any).object.visible = true;
    }
    modelInfo.visible = true;
    
    if (modelInfo.modelType === 'frag') {
      this.fragments.update(true);
      this.disableCulling(modelInfo.model as FRAGS.FragmentsModel);
    }
    
    this.map.triggerRepaint();
    
  }
  
  hideModel(modelId: string): void {
    const modelInfo = this.models.get(modelId);
    if (!modelInfo) {
      console.warn(`⚠️ Model not found: ${modelId}`);
      return;
    }
    
    if (modelInfo.modelType === 'gltf') {
      (modelInfo.model as THREE.Group).visible = false;
    } else {
      (modelInfo.model as any).object.visible = false;
    }
    modelInfo.visible = false;
    
    // Marker 現在在 folderMarkers 中管理
    // if (modelInfo.marker) {
    //   modelInfo.marker.getElement().style.display = 'none';
    // }
    
    // 更新 Fragments
    this.fragments.update(true);
    
    // 注意：隱藏時不需要禁用 culling，因為模型已經被設置為不可見
    
    this.map.triggerRepaint();
    
  }
  
  toggleModel(modelId: string): void {
    const modelInfo = this.models.get(modelId);
    if (!modelInfo) {
      console.warn(`⚠️ Model not found: ${modelId}`);
      return;
    }
    
    if (modelInfo.visible) {
      this.hideModel(modelId);
    } else {
      this.showModel(modelId);
    }
  }
  
  showAllModels(): void {
    this.models.forEach((_, modelId) => this.showModel(modelId));
  }
  
  hideAllModels(): void {
    this.models.forEach((_, modelId) => this.hideModel(modelId));
  }
  
  /**
   * 設置所有模型的透明度（直接修改材質）
   */
  async setAllModelsTransparency(opacity: number): Promise<void> {
    for (const [modelId, modelInfo] of this.models) {
      if (modelInfo.modelType === 'gltf') continue; // GLTF 不支援
      if (modelInfo.model) {
        try {
          // 獲取所有元素
          const allIds = await this.getAllElements(modelInfo.model as FRAGS.FragmentsModel);
          
          if (opacity < 1.0) {
            // 透明模式: 使用 Fragments highlight API
            const transparentMaterial: FRAGS.MaterialDefinition = {
              color: new THREE.Color(0xcccccc),      // 淺灰色
              renderedFaces: FRAGS.RenderedFaces.TWO, // 雙面渲染
              opacity: opacity,                       // 自定義透明度
              transparent: true,                      // 啟用透明
            };
            
            await (modelInfo.model as any).highlight(allIds, transparentMaterial);
          } else {
            // 不透明模式: 重置到原始狀態
            await (modelInfo.model as any).resetHighlight(allIds);
            
            // 保持 frustum culling 禁用
            (modelInfo.model as any).object.traverse((child: any) => {
              if (child.isMesh || child.isObject3D) {
                child.frustumCulled = false;
              }
            });
          }
        } catch (error) {
          console.error(`❌ Error setting transparency for model ${modelId}:`, error);
        }
      }
    }
    
    // 強制更新渲染
    await this.fragments.update(true);
  }
  
  /**
   * 設置所有模型的 Neon Light 顏色
   * @param color - 顏色（例如：'#ff0000' 紅色），傳入 null 則移除發光效果
   * @param intensity - 發光強度（0-1），預設 0.5
   */
  async setAllModelsNeonColor(color: string | null, intensity: number = 0.5): Promise<void> {
    for (const [modelId, modelInfo] of this.models) {
      if (modelInfo.modelType === 'gltf') continue; // GLTF 不支援
      if (modelInfo.model) {
        try {
          // 遍歷模型的所有 mesh
          (modelInfo.model as any).object.traverse((child: any) => {
            if (child instanceof THREE.Mesh) {
              // 處理材質（可能是單個材質或材質數組）
              const materials = Array.isArray(child.material) 
                ? child.material 
                : [child.material];
              
              materials.forEach((material: any) => {
                if (material) {
                  // 設置或移除 emissive（發光）顏色
                  if (material.emissive !== undefined) {
                    if (color === null) {
                      // 移除發光效果，恢復原本顏色
                      material.emissive.setHex(0x000000);
                      material.emissiveIntensity = 0;
                    } else {
                      // 設置發光顏色
                      const neonColor = new THREE.Color(color);
                      material.emissive = neonColor.clone();
                      material.emissiveIntensity = intensity;
                    }
                  }
                  material.needsUpdate = true;
                }
              });
            }
          });
        } catch (error) {
          console.error(`❌ Error setting neon color for model ${modelId}:`, error);
        }
      }
    }
  }
  
  // ========================================
  // 模型移除
  // ========================================
  
  removeModel(modelId: string): void {
    const modelInfo = this.models.get(modelId);
    if (!modelInfo) {
      console.warn(`⚠️ Model not found: ${modelId}`);
      return;
    }
    
    // 從場景移除模型對象
    if (modelInfo.modelType === 'frag') {
      this.scene.remove((modelInfo.model as any).object);
    }
    
    // Marker 現在在 folderMarkers 中管理
    // if (modelInfo.marker) {
    //   modelInfo.marker.remove();
    // }
    
    // 從管理器移除
    this.models.delete(modelId);
    
    // 更新 Fragments
    this.fragments.update(true);
    this.map.triggerRepaint();
  }
  
  removeAllModels(): void {
    const modelIds = Array.from(this.models.keys());
    modelIds.forEach(id => this.removeModel(id));
  }
  
  // ========================================
  // 飛去模型位置
  // ========================================
  
  flyToModel(
    modelId: string,
    options?: {
      zoom?: number;
      pitch?: number;
      bearing?: number;
      duration?: number;
    }
  ): void {
    const modelInfo = this.models.get(modelId);
    if (!modelInfo) {
      console.warn(`⚠️ Model not found: ${modelId}`);
      return;
    }
    
    const opts = {
      zoom: options?.zoom || 18,
      pitch: options?.pitch || 60,
      bearing: options?.bearing || -90,
      duration: options?.duration || 2000
    };
    
    this.map.flyTo({
      center: [modelInfo.config.position.lng, modelInfo.config.position.lat],
      zoom: opts.zoom,
      pitch: opts.pitch,
      bearing: opts.bearing,
      duration: opts.duration,
      essential: true
    });
  }
  
  // ========================================
  // 模型查詢
  // ========================================
  
  getModel(modelId: string): FRAGS.FragmentsModel | THREE.Group | undefined {
    return this.models.get(modelId)?.model;
  }
  
  getModelInfo(modelId: string): LoadedModelInfo | undefined {
    return this.models.get(modelId);
  }
  
  getModelIds(): string[] {
    return Array.from(this.models.keys());
  }
  
  getVisibleModels(): string[] {
    return Array.from(this.models.entries())
      .filter(([_, info]) => info.visible)
      .map(([id, _]) => id);
  }
  
  getModelCount(): number {
    return this.models.size;
  }
  
  // ========================================
  // 渲染更新（在 Mapbox Custom Layer 的 render 函數中調用）
  // ========================================
  
  updateTransforms(matrix: number[]): void {
    const m = new THREE.Matrix4().fromArray(matrix);
    
    this.models.forEach((modelInfo) => {
      const { transform } = modelInfo;
      
      // 建立旋轉矩陣
      const rotationX = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(1, 0, 0),
        transform.rotateX
      );
      const rotationY = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(0, 1, 0),
        transform.rotateY
      );
      const rotationZ = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(0, 0, 1),
        transform.rotateZ
      );
      
      // 建立局部變換矩陣
      const l = new THREE.Matrix4()
        .makeTranslation(
          transform.translateX,
          transform.translateY,
          transform.translateZ
        )
        .scale(
          new THREE.Vector3(
            transform.scale,
            -transform.scale,  // Y 軸翻轉
            transform.scale
          )
        )
        .multiply(rotationX)
        .multiply(rotationY)
        .multiply(rotationZ);
      
      // 組合投影矩陣（注意：這裡不直接設置 camera.projectionMatrix）
      // 實際的投影矩陣應該在 render 函數中統一設置
    });
  }
  
  // ========================================
  // 輔助方法
  // ========================================
  
  private disableCulling(model: FRAGS.FragmentsModel): void {
    // 優化：使用更高效的遍歷方式，減少重複操作
    model.object.traverse((child) => {
      // 統一設置：禁用視錐剔除並強制可見
      child.frustumCulled = false;
      child.visible = true;
      
      // 處理 LOD 對象
      if ((child as any).isLOD) {
        // 完全禁用 LOD 自動更新（提升性能）
        (child as any).autoUpdate = false;
        
        // 處理 LOD 的子對象（使用 for 循環而非 forEach，性能更好）
        const children = child.children;
        for (let i = 0; i < children.length; i++) {
          const lodChild = children[i];
          lodChild.visible = true;
          lodChild.frustumCulled = false;
          
          // 如果是 Mesh，確保設置正確
          if (lodChild instanceof THREE.Mesh) {
            lodChild.frustumCulled = false;
            lodChild.visible = true;
          }
        }
      }
      
      // 處理 Mesh 對象（已經在上面統一設置了，這裡可以簡化）
      if (child instanceof THREE.Mesh) {
        // 性能優化：跳過 bounding sphere/box 計算以提升加載速度
        // 由於我們已經設置了 frustumCulled = false，理論上不需要擴大包圍球
        // 計算 bounding sphere/box 對於大型模型可能非常耗時（數秒甚至更長）
        // 如果後續發現模型仍會消失，可以取消下面的註釋來啟用擴大包圍球
        
        // if (child.geometry) {
        //   // 僅在 bounding sphere 已存在時才擴大（避免計算）
        //   if (child.geometry.boundingSphere) {
        //     child.geometry.boundingSphere.radius *= 1000;
        //   }
        //   if (child.geometry.boundingBox) {
        //     const center = child.geometry.boundingBox.getCenter(new THREE.Vector3());
        //     const size = child.geometry.boundingBox.getSize(new THREE.Vector3());
        //     child.geometry.boundingBox.setFromCenterAndSize(center, size.multiplyScalar(1000));
        //   }
        // }
      }
    });
    
  }
  
  private createPoint(config: ModelConfig): mapboxgl.Marker {
    // 創建彩色圓點標記元素
    const el = document.createElement('div');
    el.className = 'model-point';
    el.style.backgroundColor = config.color || '#ff0000';
    el.title = config.name;
    
    // 檢查 modelMarkersHidden 狀態（可能由 share page 設定）
    const shouldHide = (window as any).modelMarkersHidden || false;
    el.style.display = shouldHide ? 'none' : 'block';
    
    // 點擊圓點飛去模型
    el.addEventListener('click', () => {
      this.flyToModel(config.id);
    });
    
    // 創建並添加圓點標記
    const point = new mapboxgl.Marker(el)
      .setLngLat([config.position.lng, config.position.lat])
      .addTo(this.map);
    
    return point;
  }
  
  private createMarker(config: ModelConfig): mapboxgl.Marker {
    // 創建 Neon 風格標記元素
    const el = document.createElement('div');
    el.className = 'model-marker';
    
    // 檢查 modelMarkersHidden 狀態（可能由 share page 設定）
    const shouldHide = (window as any).modelMarkersHidden || false;
    el.style.display = shouldHide ? 'none' : 'block';
    
    // 創建 Folder 名稱（優先使用 folderName，其次是 folder，最後是 name）
    const folderDiv = document.createElement('div');
    folderDiv.className = 'marker-folder';
    const folderName = (config as any).folderName || config.folder || config.name;
    folderDiv.textContent = folderName;
    
    // 創建狀態文字欄位（使用 folder 名稱作為識別）
    const statusDiv = document.createElement('div');
    statusDiv.className = 'marker-status';
    statusDiv.textContent = 'Normal';
    // 使用 folderName 作為 ID（實際的 folder 名稱，不是 folder ID）
    const folderIdentifier = (config as any).folderName || config.folder || config.name;
    statusDiv.setAttribute('data-folder-id', folderIdentifier);
    
    // 組合元素
    el.appendChild(folderDiv);
    el.appendChild(statusDiv);
    
    // 點擊標記飛去模型
    el.addEventListener('click', () => {
      this.flyToModel(config.id);
    });
    
    // 創建並添加標記（偏移位置避免重疊）
    const marker = new mapboxgl.Marker(el, {
      offset: [0, -30]  // 向上偏移，避免與圓點重疊
    })
      .setLngLat([config.position.lng, config.position.lat])
      .addTo(this.map);
    
    return marker;
  }
  
  
  // ========================================
  // 獲取 Fragments 管理器
  // ========================================
  
  getFragments(): FRAGS.FragmentsModels {
    return this.fragments;
  }
  
  // ========================================
  // 頂點顏色管理方法
  // ========================================
  
  /**
   * 初始化模型的頂點顏色系統
   */
  private initVertexColorForModel(modelId: string, model: FRAGS.FragmentsModel): void {
    const meshDataList: MeshColorData[] = [];
    let totalMeshes = 0;
    let meshesWithId = 0;
    
    model.object.traverse((child) => {
      if (!(child as THREE.Mesh).isMesh) return;
      
      totalMeshes++;
      const mesh = child as THREE.Mesh;
      const geometry = mesh.geometry;
      
      const idAttr = geometry.attributes.id;
      if (!idAttr || !idAttr.array) return;
      
      meshesWithId++;
      
      // 立即保存 id 數據
      const localIdToVertices = new Map<number, number[]>();
      
      for (let i = 0; i < idAttr.count; i++) {
        const localId = idAttr.array[i * idAttr.itemSize];
        
        if (!localIdToVertices.has(localId)) {
          localIdToVertices.set(localId, []);
        }
        localIdToVertices.get(localId)!.push(i);
      }
      
      // 創建 color attribute
      let colorAttr = geometry.attributes.color as THREE.BufferAttribute;
      
      if (!colorAttr) {
        const colorArray = new Float32Array(geometry.attributes.position.count * 3);
        for (let i = 0; i < geometry.attributes.position.count; i++) {
          colorArray[i * 3 + 0] = 1;
          colorArray[i * 3 + 1] = 1;
          colorArray[i * 3 + 2] = 1;
        }
        
        colorAttr = new THREE.BufferAttribute(colorArray, 3);
        colorAttr.onUploadCallback = function() {};
        geometry.setAttribute('color', colorAttr);
      } else if (!colorAttr.array) {
        const colorArray = new Float32Array(colorAttr.count * 3);
        for (let i = 0; i < colorAttr.count; i++) {
          colorArray[i * 3 + 0] = 1;
          colorArray[i * 3 + 1] = 1;
          colorArray[i * 3 + 2] = 1;
        }
        
        const newColorAttr = new THREE.BufferAttribute(colorArray, 3);
        newColorAttr.onUploadCallback = function() {};
        geometry.setAttribute('color', newColorAttr);
        colorAttr = newColorAttr;
      }
      
      // 啟用 vertexColors
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach((mat) => {
        if (mat) {
          mat.vertexColors = true;
          mat.needsUpdate = true;
        }
      });
      
      meshDataList.push({
        mesh,
        colorAttribute: colorAttr,
        localIdToVertices
      });
    });
    
    this.vertexColorData.set(modelId, meshDataList);
    this.coloredElements.set(modelId, new Map());
  }
  
  /**
   * 為指定 localId 設置顏色（PUBLIC 方法）
   */
  public setVertexColor(modelId: string, localIds: number[], options: VertexColorOptions): void {
    const meshDataList = this.vertexColorData.get(modelId);
    
    if (!meshDataList) {
      console.error(`❌ [VertexColor] 模型未初始化: ${modelId}`);
      return;
    }
    
    const color = typeof options.color === "string" 
      ? new THREE.Color(options.color) 
      : options.color;
    
    let totalVerticesModified = 0;
    
    for (const meshData of meshDataList) {
      const { colorAttribute, localIdToVertices } = meshData;
      
      for (const localId of localIds) {
        const vertices = localIdToVertices.get(localId);
        
        if (!vertices || vertices.length === 0) continue;
        
        for (const vIdx of vertices) {
          colorAttribute.array[vIdx * 3 + 0] = color.r;
          colorAttribute.array[vIdx * 3 + 1] = color.g;
          colorAttribute.array[vIdx * 3 + 2] = color.b;
        }
        
        totalVerticesModified += vertices.length;
        colorAttribute.needsUpdate = true;
      }
    }
    
    const colorMap = this.coloredElements.get(modelId)!;
    for (const localId of localIds) {
      colorMap.set(localId, options);
    }
  }
  
  /**
   * 重置顏色（PUBLIC 方法）
   */
  public resetVertexColor(modelId: string, localIds?: number[]): void {
    const meshDataList = this.vertexColorData.get(modelId);
    
    if (!meshDataList) return;
    
    const colorMap = this.coloredElements.get(modelId)!;
    const targetLocalIds = localIds || Array.from(colorMap.keys());
    
    const whiteColor = new THREE.Color(1, 1, 1);
    
    for (const meshData of meshDataList) {
      const { colorAttribute, localIdToVertices } = meshData;
      
      for (const localId of targetLocalIds) {
        const vertices = localIdToVertices.get(localId);
        
        if (!vertices || vertices.length === 0) continue;
        
        for (const vIdx of vertices) {
          colorAttribute.array[vIdx * 3 + 0] = whiteColor.r;
          colorAttribute.array[vIdx * 3 + 1] = whiteColor.g;
          colorAttribute.array[vIdx * 3 + 2] = whiteColor.b;
        }
        
        colorAttribute.needsUpdate = true;
      }
    }
    
    for (const localId of targetLocalIds) {
      colorMap.delete(localId);
    }
  }
  
  /**
   * 獲取所有已加載的模型
   * @returns Map of model ID to LoadedModelInfo
   */
  public getAllModels(): Map<string, LoadedModelInfo> {
    return this.models;
  }
  
  /**
   * 獲取所有 folder markers
   * @returns Map of folder name to marker info
   */
  public getFolderMarkers(): Map<string, any> {
    return this.folderMarkers;
  }
  
  /**
   * 獲取模型中的所有元素 ID (帶緩存)
   * 根據 ISOLATE_TRANSPARENT_GUIDE.md
   */
  private async getAllElements(model: FRAGS.FragmentsModel): Promise<number[]> {
    // 獲取所有類別
    const allCategories = await model.getCategories();
    
    // 獲取所有類別的所有元素
    const allElementsMap = await model.getItemsOfCategories(
      allCategories.map(cat => new RegExp(`^${cat}$`))
    );
    
    // 展平成單一陣列（使用 concat 避免堆疊溢出）
    const allElements: number[] = [];
    for (const ids of Object.values(allElementsMap)) {
      if (Array.isArray(ids)) {
        // 使用 concat 而非 spread operator，避免大陣列導致堆疊溢出
        for (let i = 0; i < ids.length; i++) {
          allElements.push(ids[i]);
        }
      }
    }
    
    return allElements;
  }
  
}
